/**
 * AiCloud - Kurulu Skills
 */

import axios from 'axios'
import { Check, Trash2, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { API_URL } from '../../../config/api'

interface InstalledSkill {
  id: number
  skill_id: number
  name: string
  description: string
  category: string
  version: string
  installed_at: string
  is_enabled: boolean
}

export default function InstalledSkillsPage() {
  const [skills, setSkills] = useState<InstalledSkill[]>([])
  const [loading, setLoading] = useState(true)
  const [pendingUninstall, setPendingUninstall] = useState<number | null>(null)

  const fetchSkills = async () => {
    try {
      const token = localStorage.getItem('token')
      const resp = await axios.get(`${API_URL}/api/aicloud/skills/installed`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSkills((resp.data as { skills: InstalledSkill[] }).skills ?? [])
    } catch {
      setSkills([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void fetchSkills() }, [])

  const handleUninstall = async (id: number) => {
    setPendingUninstall(null)
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/api/aicloud/skills/${id}/uninstall`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSkills(prev => prev.filter(s => s.id !== id))
    } catch {
      // ignore
    }
  }

  const handleToggle = async (id: number, enabled: boolean) => {
    try {
      const token = localStorage.getItem('token')
      await axios.patch(`${API_URL}/api/aicloud/skills/${id}/toggle`, { enabled: !enabled }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSkills(prev => prev.map(s => s.id === id ? { ...s, is_enabled: !enabled } : s))
    } catch {
      // ignore
    }
  }

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>Installed Skills</h2>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Skills installed on your account.</p>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading...</div>
      ) : skills.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', background: 'var(--card-bg)', border: '1px dashed var(--border-color)', borderRadius: '10px' }}>
          <Zap size={32} color="#3b82f6" style={{ margin: '0 auto 12px' }} />
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>No skills installed yet.</p>
          <Link to="/aicloud/skills"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#3b82f6', color: 'white', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: 600 }}>
            Browse Marketplace
          </Link>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {skills.map(skill => (
            <div key={skill.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
              <Zap size={16} color="#3b82f6" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: 600 }}>{skill.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{skill.category} · v{skill.version}</div>
              </div>
              <button
                onClick={() => { void handleToggle(skill.id, skill.is_enabled) }}
                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', background: skill.is_enabled ? 'rgba(16,185,129,0.1)' : 'var(--body-bg)', border: `1px solid ${skill.is_enabled ? 'rgba(16,185,129,0.3)' : 'var(--border-color)'}`, borderRadius: '6px', cursor: 'pointer', color: skill.is_enabled ? '#10b981' : 'var(--text-secondary)', fontSize: '12px' }}>
                {skill.is_enabled ? <><Check size={11} /> Enabled</> : 'Disabled'}
              </button>
              <button onClick={() => setPendingUninstall(skill.id)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px' }}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      {/* Uninstall confirm */}
      {pendingUninstall !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '24px', maxWidth: '320px', width: '100%' }}>
            <p style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '20px' }}>Uninstall this skill?</p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => { void handleUninstall(pendingUninstall) }}
                style={{ flex: 1, padding: '8px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                Uninstall
              </button>
              <button onClick={() => setPendingUninstall(null)}
                style={{ flex: 1, padding: '8px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
